import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

export interface PageAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
  variant?: "default" | "outline" | "destructive" | "secondary" | "ghost";
  testId?: string;
}

interface PageActionsContextValue {
  actions: PageAction[];
  setActions: (actions: PageAction[]) => void;
  updateHandlers: (actions: PageAction[]) => void;
  clearActions: () => void;
  /** Get the current onClick handler for an action by ID (always returns latest) */
  getActionHandler: (id: string) => (() => void) | undefined;
}

const PageActionsContext = createContext<PageActionsContextValue | null>(null);

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActionsState] = useState<PageAction[]>([]);
  // Store the latest actions in a ref to always have fresh onClick handlers
  const actionsRef = useRef<PageAction[]>([]);

  const setActions = useCallback((newActions: PageAction[]) => {
    // Update the ref with latest actions (for fresh onClick handlers)
    actionsRef.current = newActions;
    // Update state to trigger re-render
    setActionsState(newActions);
  }, []);

  // Update only the ref (no re-render) - used when only onClick handlers changed
  const updateHandlers = useCallback((newActions: PageAction[]) => {
    actionsRef.current = newActions;
  }, []);

  const clearActions = useCallback(() => {
    actionsRef.current = [];
    setActionsState([]);
  }, []);

  // Get handler from ref to ensure it's always the latest
  const getActionHandler = useCallback((id: string) => {
    return actionsRef.current.find(a => a.id === id)?.onClick;
  }, []);

  return (
    <PageActionsContext.Provider value={{ actions, setActions, updateHandlers, clearActions, getActionHandler }}>
      {children}
    </PageActionsContext.Provider>
  );
}

export function usePageActions(): PageActionsContextValue {
  const context = useContext(PageActionsContext);
  if (!context) {
    throw new Error("usePageActions must be used within a PageActionsProvider");
  }
  return context;
}
